import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Trash2, Stethoscope, Edit } from 'lucide-react';
import { toast } from 'sonner';

export default function DoctorsManagement() {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [formData, setFormData] = useState({
    user_id: '',
    hospital_id: '',
    department_id: '',
    specialization: '',
    availability: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [doctorsRes, hospitalsRes, deptRes] = await Promise.all([
        axiosInstance.get('/doctors'),
        axiosInstance.get('/hospitals'),
        axiosInstance.get('/departments')
      ]);
      setDoctors(doctorsRes.data);
      setHospitals(hospitalsRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    try {
      // Auto-set hospital_id for hospital admins
      const submitData = { ...formData };
      if (user?.role === 'hospital_admin' && hospitals.length > 0) {
        submitData.hospital_id = hospitals[0].id;
      }
      
      await axiosInstance.post('/doctors', submitData);
      toast.success('Doctor added successfully');
      setShowAddDialog(false);
      setFormData({ user_id: '', hospital_id: '', department_id: '', specialization: '', availability: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add doctor');
    }
  };

  const handleEdit = (doctor) => {
    setFormData(doctor);
    setShowAddDialog(true);
  };

  const handleDelete = async (doctorId) => {
    if (!window.confirm('Are you sure you want to delete this doctor?')) return;
    
    try {
      await axiosInstance.delete(`/doctors/${doctorId}`);
      toast.success('Doctor deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete doctor');
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-manrope font-bold mb-2" data-testid="doctors-title">
              Doctors Management
            </h1>
            <p className="text-slate-600">Manage doctor profiles and availability</p>
          </div>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-doctor-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Doctor
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Add New Doctor</DialogTitle>
                <DialogDescription>Enter doctor details below</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddDoctor} className="space-y-4">
                <div className="space-y-2">
                  <Label>Email / User ID</Label>
                  <Input
                    value={formData.user_id}
                    onChange={(e) => setFormData({...formData, user_id: e.target.value})}
                    required
                    placeholder="doctor@example.com"
                  />
                </div>
                
                {user?.role === 'super_admin' && (
                  <div className="space-y-2">
                    <Label>Hospital</Label>
                    <Select
                      value={formData.hospital_id}
                      onValueChange={(value) => setFormData({...formData, hospital_id: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select hospital" />
                      </SelectTrigger>
                      <SelectContent>
                        {hospitals.map((h, i) => (
                          <SelectItem key={i} value={h.id}>{h.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {user?.role === 'hospital_admin' && hospitals.length > 0 && (
                  <div className="space-y-2">
                    <Label>Hospital</Label>
                    <Input value={hospitals[0].name} disabled className="bg-slate-100" />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Department</Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(value) => setFormData({...formData, department_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments.map((d, i) => (
                        <SelectItem key={i} value={d.id}>{d.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Specialization</Label>
                  <Input
                    value={formData.specialization}
                    onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                    required
                    placeholder="e.g., Cardiologist"
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="submit">Add Doctor</Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid gap-4">
          {doctors.map((doctor, index) => (
            <Card key={index}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <Stethoscope className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Dr. {doctor.user_id}</h3>
                      <p className="text-sm text-slate-600">{doctor.specialization}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                        <span>Hospital ID: {doctor.hospital_id}</span>
                        <span>Department ID: {doctor.department_id}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(doctor)}
                    data-testid={`edit-doctor-${index}`}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(doctor.id)}
                    data-testid={`delete-doctor-${index}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {doctors.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-center text-slate-500">
                No doctors found. Add your first doctor to get started.
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}